/**
 * Help System Demo Page
 * Showcases the contextual help system features and usage
 */

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  HelpCircle,
  BookOpen,
  Lightbulb,
  CheckCircle2,
  Code,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { HelpButton } from "@/components/help-button";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { useNavigation } from "@/components/layout/app-layout";
import { getAllHelpPageIds } from "@/data/help-content";

export function HelpSystemDemoPage() {
  const { navigateTo } = useNavigation();
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("stock-items");
  const [selectedDemo, setSelectedDemo] = useState<string | null>(null);

  const availablePages = getAllHelpPageIds();

  const features = [
    {
      icon: BookOpen,
      title: "Comprehensive Content",
      description: "Detailed functionality explanations, step-by-step guides, and FAQs for every page.",
      color: "text-blue-600",
      bgColor: "bg-blue-50 dark:bg-blue-950",
    },
    {
      icon: HelpCircle,
      title: "Context-Aware",
      description: "Help content automatically adapts to the current page you're viewing.",
      color: "text-purple-600",
      bgColor: "bg-purple-50 dark:bg-purple-950",
    },
    {
      icon: Lightbulb,
      title: "Pro Tips & Best Practices",
      description: "Learn shortcuts, best practices, and expert tips to work more efficiently.",
      color: "text-amber-600",
      bgColor: "bg-amber-50 dark:bg-amber-950",
    },
    {
      icon: CheckCircle2,
      title: "Easy to Use",
      description: "Simple help button in page headers provides instant access to relevant information.",
      color: "text-green-600",
      bgColor: "bg-green-50 dark:bg-green-950",
    },
  ];

  const codeExamples = {
    basic: `// Add help to any page in 3 simple steps:

// 1. Import the components
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { HelpButton } from "@/components/help-button";
import { useContextualHelp } from "@/hooks/use-contextual-help";

// 2. Initialize the hook
const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = 
  useContextualHelp("your-page-id");

// 3. Add the button and drawer
<HelpButton onClick={toggleHelp} showLabel />
<ContextualHelpDrawer
  open={isHelpOpen}
  onOpenChange={setIsHelpOpen}
  helpContent={helpContent}
  onNavigateToPage={(pageId) => navigateTo(pageId)}
/>`,
    content: `// Define help content in /data/help-content.ts
export const helpContentDatabase = {
  "your-page-id": {
    pageId: "your-page-id",
    title: "Your Page Title",
    description: "Brief description",
    
    functionality: {
      overview: "What this page does...",
      keyFeatures: [
        "Feature 1",
        "Feature 2",
      ],
    },
    
    userGuide: {
      commonTasks: [
        {
          step: 1,
          title: "First Task",
          description: "How to do it...",
          tips: ["Helpful tip"],
        },
      ],
    },
    
    faq: [
      {
        question: "Common question?",
        answer: "Clear answer...",
      },
    ],
  },
};`,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Help System Demo</h1>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              <Sparkles className="h-3 w-3 mr-1" />
              Interactive Demo
            </Badge>
          </div>
          <HelpButton onClick={toggleHelp} variant="outline" size="default" showLabel />
        </div>
        <p className="text-muted-foreground mt-2">
          Explore the contextual help system and learn how to implement it in your pages.
        </p>
      </div>

      {/* Try It Now Section */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-primary" />
            Try It Now!
          </CardTitle>
          <CardDescription>
            Click the "Help" button in the top right corner to see the contextual help drawer in action.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Button onClick={toggleHelp} size="lg" className="gap-2">
              <HelpCircle className="h-4 w-4" />
              Open Help Drawer
            </Button>
            <p className="text-sm text-muted-foreground">
              This will show help content for the Stock Items page as an example.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Features Grid */}
      <div>
        <h2 className="text-2xl font-semibold mb-4">Key Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${feature.bgColor}`}>
                      <Icon className={`h-5 w-5 ${feature.color}`} />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                      <CardDescription className="mt-2">{feature.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Available Help Pages */}
      <Card>
        <CardHeader>
          <CardTitle>Pages with Help Content</CardTitle>
          <CardDescription>
            The following pages have contextual help available. Click on any page to navigate and try the help system.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {availablePages.map((pageId) => (
              <Button
                key={pageId}
                variant="outline"
                className="justify-between"
                onClick={() => navigateTo(pageId)}
              >
                <span className="capitalize">{pageId.replace(/-/g, " ")}</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Implementation Guide */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5" />
            Implementation Guide
          </CardTitle>
          <CardDescription>
            Learn how to add the help system to your pages
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="basic">Basic Setup</TabsTrigger>
              <TabsTrigger value="content">Content Structure</TabsTrigger>
            </TabsList>
            <TabsContent value="basic" className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Badge className="mt-1">1</Badge>
                  <div>
                    <p className="font-medium">Import Components</p>
                    <p className="text-sm text-muted-foreground">
                      Import the help drawer, button, and hook into your page component.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Badge className="mt-1">2</Badge>
                  <div>
                    <p className="font-medium">Initialize Hook</p>
                    <p className="text-sm text-muted-foreground">
                      Call useContextualHelp with your page ID to get help content and state management.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Badge className="mt-1">3</Badge>
                  <div>
                    <p className="font-medium">Add UI Components</p>
                    <p className="text-sm text-muted-foreground">
                      Place the HelpButton in your header and the ContextualHelpDrawer at the end of your component.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-muted rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs">
                  <code>{codeExamples.basic}</code>
                </pre>
              </div>
            </TabsContent>
            <TabsContent value="content" className="space-y-4">
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Help content is organised into three main sections:
                </p>
                <div className="flex items-start gap-3">
                  <Badge variant="outline">Functionality</Badge>
                  <p className="text-sm text-muted-foreground">
                    Overview, key features, and common use cases
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Badge variant="outline">User Guide</Badge>
                  <p className="text-sm text-muted-foreground">
                    Step-by-step instructions and pro tips
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Badge variant="outline">FAQ</Badge>
                  <p className="text-sm text-muted-foreground">
                    Frequently asked questions and answers
                  </p>
                </div>
              </div>
              <div className="bg-muted rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs">
                  <code>{codeExamples.content}</code>
                </pre>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Benefits */}
      <Card>
        <CardHeader>
          <CardTitle>Benefits</CardTitle>
          <CardDescription>Why use the contextual help system?</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Improved User Experience</p>
                <p className="text-sm text-muted-foreground">
                  Users get help without leaving their current workflow
                </p>
              </div>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Reduced Support Tickets</p>
                <p className="text-sm text-muted-foreground">
                  Self-service help reduces the need for support intervention
                </p>
              </div>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Faster Onboarding</p>
                <p className="text-sm text-muted-foreground">
                  New users can learn the system at their own pace
                </p>
              </div>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Consistent Documentation</p>
                <p className="text-sm text-muted-foreground">
                  Centralized help content ensures consistency across the application
                </p>
              </div>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Easy Maintenance</p>
                <p className="text-sm text-muted-foreground">
                  Update help content in one place, reflected everywhere
                </p>
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Documentation Link */}
      <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-blue-600" />
            Full Documentation
          </CardTitle>
          <CardDescription>
            For complete implementation details, content guidelines, and best practices, refer to the full documentation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Location: <code className="bg-muted px-2 py-1 rounded">/src/docs/CONTEXTUAL_HELP_SYSTEM.md</code>
          </p>
          <Button variant="outline" className="gap-2">
            <BookOpen className="h-4 w-4" />
            View Documentation
          </Button>
        </CardContent>
      </Card>

      {/* Contextual Help Drawer */}
      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        helpContent={helpContent}
        onNavigateToPage={(pageId) => navigateTo(pageId)}
      />
    </div>
  );
}
